import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useCity } from '../../contexts/CityContext';
import { useChat } from '../../contexts/ChatContext';
import CustomerDetailsModal from './CustomerDetailsModal';
import NewCustomerModal from './NewCustomerModal';
import config from '../../config';
import CustomerDialNumberPicker from '../../components/CustomerDialNumberPicker/CustomerDialNumberPicker';
import {
  buildCustomerDialOptions,
  fetchCustomerExtraPhones,
} from '../../utils/customerVoiceCall';
import opsWebSocket from '../../services/opsWebSocket';
import s from './Customers.module.scss';
import {
  customerHasCallSummary,
  formatLatestCallLabel,
} from './voiceCallSummary';
import {
  batchHasActivity,
  customerNeedsFollowUpCall,
  getCustomerBatchState,
  normalizeBatchStatus,
  normalizePendingCallCounts,
} from './voiceCallBatchStatus';

// ============================================================================
// CONFIGURATION CONSTANTS
// ============================================================================
const CONFIG = {
  API_BASE_URL: config.customerApiBase,
  POLLING_INTERVAL: 60000, // 60s auto-refresh to reduce load
  DEBOUNCE_DELAY: 400,
};
const VOICE_CALLS_ENABLED = config.voiceCallServerEnabled;

// ============================================================================
// CUSTOM HOOKS
// ============================================================================

// Debounced value hook - delays updates until user stops typing
const useDebounce = (value, delay = CONFIG.DEBOUNCE_DELAY) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
};

// API fetch hook with abort controller for cleanup
const useFetchData = (url, pollingInterval = null) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);

  const fetchData = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      const response = await fetch(url, {
        signal: abortControllerRef.current.signal,
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      setData(result);
      setError(null);
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Fetch error:', err);
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [url]);

  // Initial fetch when URL changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle background polling independently
  useEffect(() => {
    let interval;
    if (pollingInterval) {
      interval = setInterval(fetchData, pollingInterval);
    }
    return () => {
      if (interval) clearInterval(interval);
      // Removed the immediate abort on cleanup here so we don't kill manual syncs
      // when the polling interval toggles between null and 60s.
    };
  }, [fetchData, pollingInterval]);

  return { data, loading, error, refetch: fetchData };
};

const hasDialablePhone = (customer) => {
  const phone = customer?.phone;
  return !!phone && phone !== 'N/A';
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const Customers = () => {
  const history = useHistory();
  const { selectedCity } = useCity();
  const { openChat } = useChat();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState(null); // 'flagged' | 'starred' | 'activeSessions' | 'successful' | 'failed' | 'blocked' | 'marketing_opt_out' | 'auto_reply_flagged' | null
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const [menuAlignRight, setMenuAlignRight] = useState(false);
  const moreFiltersRef = useRef(null);
  const moreBtnRef = useRef(null);
  const [callStatusFilter, setCallStatusFilter] = useState('all'); // 'all' | 'called' | 'not_called'
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [activeSessions, setActiveSessions] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const refreshIntervalSeconds = CONFIG.POLLING_INTERVAL / 1000;
  const [nextRefreshIn, setNextRefreshIn] = useState(refreshIntervalSeconds);
  const [pageInput, setPageInput] = useState(1);
  const [newCustomerModalOpen, setNewCustomerModalOpen] = useState(false);
  const [sendingWelcomeTemplate, setSendingWelcomeTemplate] = useState(new Set());
  const [callingCustomers, setCallingCustomers] = useState(new Set());
  const [selectedCallIds, setSelectedCallIds] = useState(new Set());

  // Batch Call All state — backed by call_server.py
  const [batchStatus, setBatchStatus] = useState(() => normalizeBatchStatus(null));
  const [pendingCallCounts, setPendingCallCounts] = useState({});
  const applyBatchStatus = useCallback((status) => {
    const normalized = normalizeBatchStatus(status);
    setBatchStatus(normalized);
    setPendingCallCounts(normalizePendingCallCounts(status?.call_counts, normalized));
  }, []);
  const hasBatchActivity = batchHasActivity(batchStatus);
  const batchWasActiveRef = useRef(false);
  const [syncingCalled, setSyncingCalled] = useState(false);

  // Voice call modal state
  const [voiceCallModal, setVoiceCallModal] = useState({
    open: false,
    customer: null,
    callName: '',
    status: 'idle', // idle | calling | success | error
    errorMsg: '',
  });
  const [activeCustomerSims, setActiveCustomerSims] = useState([]);
  const [selectedCustomerSim, setSelectedCustomerSim] = useState('');
  const [isLoadingSims, setIsLoadingSims] = useState(false);
  const [bulkNameModal, setBulkNameModal] = useState({
    open: false,
    customers: [],
    nameOverrides: {},
    batchMode: 'start', // start | add
    status: 'idle', // idle | saving | calling | error
    errorMsg: '',
  });
  const [hasAlert, setHasAlert] = useState(false);
  const [sortBy, setSortBy] = useState('recentConversation');
  const location = useLocation();

  // Legacy deep links from Customers → Call Manager call logs
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    const phone = params.get('phone');
    if (tab === 'call-logs') {
      const qs = phone ? `?view=call-logs&phone=${encodeURIComponent(phone)}` : '?view=call-logs';
      history.replace(`/app/call-manager${qs}`);
    }
  }, [location.search, history]);
  const itemsPerPage = 20;
  const previousFlaggedCountRef = useRef(0);
  const debouncedSearchTerm = useDebounce(searchTerm);

  const apiUrl = React.useMemo(() => {
    const baseUrl = `${CONFIG.API_BASE_URL}/customers`;
    if (selectedCity && selectedCity !== 'All') {
      return `${baseUrl}?city=${encodeURIComponent(selectedCity)}`;
    }
    return baseUrl;
  }, [selectedCity]);

  // Disable background polling while a call is active so it doesn't interfere
  const currentPollingInterval = hasBatchActivity ? null : CONFIG.POLLING_INTERVAL;

  const {
    data: customersData,
    loading: customersLoading,
    error: customersError,
    refetch: refetchCustomers,
  } = useFetchData(apiUrl, currentPollingInterval);

  const {
    data: activeSessionsData,
    refetch: refetchActiveSessions,
  } = useFetchData(`${CONFIG.API_BASE_URL}/customers/active-sessions`, currentPollingInterval);

  // Conversation stats with adjustable time window
  const [statsHours, setStatsHours] = useState(1);
  const statsUrl = React.useMemo(
    () => `${CONFIG.API_BASE_URL}/conversations/stats?hours=${statsHours}`,
    [statsHours]
  );
  const {
    data: conversationStats,
    loading: statsLoading,
    refetch: refetchStats,
  } = useFetchData(statsUrl, currentPollingInterval);

  // Reset refresh countdown when new data arrives
  useEffect(() => {
    if (customersData) {
      setNextRefreshIn(refreshIntervalSeconds);
    }
  }, [customersData, refreshIntervalSeconds]);

  // Tick down the countdown every second
  useEffect(() => {
    if (hasBatchActivity) return; // Pause countdown while calls are active
    const timer = setInterval(() => {
      setNextRefreshIn((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [hasBatchActivity]);

  useEffect(() => {
    if (activeSessionsData?.customers) {
      const sessionsMap = {};
      activeSessionsData.customers.forEach(customer => {
        sessionsMap[customer.customer_id] = {
          user_id: customer.user_id,
          last_activity: customer.last_activity
        };
      });
      setActiveSessions(sessionsMap);
    }
  }, [activeSessionsData]);

  // Reset page when search, city, or active filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, selectedCity, activeFilter, sortBy, callStatusFilter]);

  const allCustomers = React.useMemo(
    () => customersData?.customers || [],
    [customersData]
  );

  // Called / Not Called filter: call_summary (voice bot stamps). Times Called column is separate.
  const isCustomerCalled = useCallback((customer) => (
    customerHasCallSummary(customer)
  ), []);

  const batchCallsCompleted = batchStatus.done.length;
  const batchCallsInProgress = batchStatus.active.length;
  const batchCallsLeft = batchStatus.queued.length;
  const batchProgressPct = batchStatus.total > 0
    ? Math.round((batchCallsCompleted / batchStatus.total) * 100)
    : 0;
  const flaggedCount = React.useMemo(
    () => allCustomers.filter(c => c.flagged).length,
    [allCustomers]
  );
  const starredCount = React.useMemo(
    () => allCustomers.filter(c => c.starred).length,
    [allCustomers]
  );

  const blockedCount = React.useMemo(
    () => allCustomers.filter(c => c.is_blocked).length,
    [allCustomers]
  );

  const marketingOptOutCount = React.useMemo(
    () => allCustomers.filter(c => c.marketing_opt_out).length,
    [allCustomers]
  );

  const b2bCount = React.useMemo(
    () => allCustomers.filter(c => c.is_b2b).length,
    [allCustomers]
  );

  const autoReplyFlaggedCount = React.useMemo(
    () => allCustomers.filter(c => c.is_auto_reply_flagged).length,
    [allCustomers]
  );

  const toggleMoreFilters = () => {
    if (!moreFiltersOpen && moreBtnRef.current) {
      const rect = moreBtnRef.current.getBoundingClientRect();
      setMenuAlignRight(rect.right + 290 > window.innerWidth - 8);
    }
    setMoreFiltersOpen(o => !o);
  };

  useEffect(() => {
    if (!moreFiltersOpen) return;
    const onDocClick = (e) => {
      if (moreFiltersRef.current && !moreFiltersRef.current.contains(e.target)) {
        setMoreFiltersOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [moreFiltersOpen]);

  useEffect(() => {
    // Trigger alert when flagged count appears or increases.
    if (flaggedCount > 0 && flaggedCount >= previousFlaggedCountRef.current) {
      setHasAlert(true);
    }
    previousFlaggedCountRef.current = flaggedCount;
  }, [flaggedCount]);

  useEffect(() => {
    const unsubFlagged = opsWebSocket.subscribe('flagged_customer', () => {
      setHasAlert(true);
    });

    const unsubIssue = opsWebSocket.subscribe('new_conversation_issue', () => {
      setHasAlert(true);
    });

    return () => {
      unsubFlagged();
      unsubIssue();
    };
  }, []);

  const filteredCustomers = React.useMemo(() => {
    let filtered = allCustomers;

    // Apply stat card filter
    if (activeFilter === 'flagged') {
      filtered = filtered.filter(c => c.flagged);
    } else if (activeFilter === 'starred') {
      filtered = filtered.filter(c => c.starred);
    } else if (activeFilter === 'blocked') {
      filtered = filtered.filter(c => c.is_blocked);
    } else if (activeFilter === 'marketing_opt_out') {
      filtered = filtered.filter(c => c.marketing_opt_out);
    } else if (activeFilter === 'auto_reply_flagged') {
      filtered = filtered.filter(c => c.is_auto_reply_flagged);
    } else if (activeFilter === 'b2b') {
      filtered = filtered.filter(c => c.is_b2b);
    } else if (activeFilter === 'activeSessions') {
      filtered = filtered.filter(c => !!activeSessions[c.id]);
    } else if (activeFilter === 'successful') {
      const ids = new Set(conversationStats?.successful_customer_ids || []);
      filtered = filtered.filter(c => ids.has(c.id));
    } else if (activeFilter === 'failed') {
      const ids = new Set(conversationStats?.failed_customer_ids || []);
      filtered = filtered.filter(c => ids.has(c.id));
    }

    // Apply call status filter
    if (callStatusFilter === 'called') {
      filtered = filtered.filter(c => isCustomerCalled(c));
    } else if (callStatusFilter === 'not_called') {
      filtered = filtered.filter(c => !isCustomerCalled(c));
    } else if (callStatusFilter === 'calling') {
      const activeOrQueued = new Set([
        ...(batchStatus?.active || []),
        ...(batchStatus?.queued || [])
      ].map(id => Number(id)));
      filtered = filtered.filter(c => activeOrQueued.has(Number(c.id)));
    }

    // Apply search filter
    if (debouncedSearchTerm) {
      const searchLower = debouncedSearchTerm.toLowerCase();
      filtered = filtered.filter((customer) => {
        const nameMatch = customer.name?.toLowerCase().includes(searchLower);
        const businessNameMatch = customer.business_name?.toLowerCase().includes(searchLower);
        const domainMatch = customer.domain?.toLowerCase().includes(searchLower);
        const phoneMatch = customer.phone?.toString().includes(debouncedSearchTerm)
          || (customer.extra_phones || []).some((entry) => (
            String(entry.phone || '').includes(debouncedSearchTerm)
            || String(entry.label || '').toLowerCase().includes(searchLower)
            || String(entry.remark || '').toLowerCase().includes(searchLower)
          ));
        const idMatch = customer.id?.toString().includes(debouncedSearchTerm);
        const callSummaryMatch = customer.call_summary
          ? customer.call_summary.toLowerCase().includes(searchLower)
          : false;
        const remarksMatch = customer.remarks
          ? customer.remarks.toLowerCase().includes(searchLower)
          : false;
        return nameMatch || businessNameMatch || domainMatch || phoneMatch || idMatch
          || callSummaryMatch || remarksMatch;
      });
    }

    const getTimeValue = (value) => {
      if (!value) return 0;
      const parsed = new Date(value).getTime();
      return Number.isNaN(parsed) ? 0 : parsed;
    };

    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === 'id') {
        return Number(b.id || 0) - Number(a.id || 0);
      }

      const aRecentTime = getTimeValue(
        a.last_conversation_at || activeSessions[a.id]?.last_activity || a.created_at
      );
      const bRecentTime = getTimeValue(
        b.last_conversation_at || activeSessions[b.id]?.last_activity || b.created_at
      );

      if (bRecentTime !== aRecentTime) {
        return bRecentTime - aRecentTime;
      }

      return Number(b.id || 0) - Number(a.id || 0);
    });

    return sorted;
  }, [allCustomers, debouncedSearchTerm, activeFilter, activeSessions, conversationStats, sortBy, callStatusFilter, isCustomerCalled, batchStatus]);

  // Pagination logic
  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
  const currentCustomers = filteredCustomers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    setPageInput(currentPage);
  }, [currentPage]);

  const handlePageInputChange = (e) => {
    setPageInput(e.target.value);
  };

  const handlePageInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      const num = parseInt(pageInput, 10);
      if (Number.isFinite(num) && num >= 1 && num <= totalPages) {
        handlePageChange(num);
      } else {
        setPageInput(currentPage);
      }
    }
  };

  const handlePageInputBlur = () => {
    setPageInput(currentPage);
  };

  // Toggle a stat card filter; clicking the same one again clears it
  const handleFilterClick = useCallback((filterType) => {
    setActiveFilter(prev => prev === filterType ? null : filterType);
  }, []);

  const handleNewCustomer = useCallback(() => {
    setNewCustomerModalOpen(true);
  }, []);

  const handleCloseNewCustomer = useCallback(() => {
    setNewCustomerModalOpen(false);
  }, []);

  const handleCustomerCreated = useCallback(() => {
    refetchCustomers(); // Refresh the customer list
  }, [refetchCustomers]);

  const handleRetry = useCallback(() => {
    refetchCustomers();
    refetchActiveSessions();
    refetchStats();
  }, [refetchCustomers, refetchActiveSessions, refetchStats]);

  const handleSyncCalledStatus = useCallback(async () => {
    setSyncingCalled(true);
    try {
      await refetchCustomers();
    } finally {
      setSyncingCalled(false);
    }
  }, [refetchCustomers]);

  const syncCalledStatusAfterBatch = useCallback(() => {
    setSyncingCalled(true);
    setTimeout(async () => {
      try {
        await refetchCustomers();
      } catch (e) {
        console.error('Auto-sync after batch failed:', e);
      } finally {
        setSyncingCalled(false);
      }
    }, 1000); // 1-second delay allows DB to fully commit call_summary and voice_call_count
  }, [refetchCustomers]);

  const handleToggle = useCallback(() => {
    setHasAlert(false);
    history.push('/app/conversations');
  }, [history]);

  const handleViewDetails = useCallback((customerId) => {
    setSelectedCustomerId(customerId);
    setDetailsModalOpen(true);
  }, []);

  const handleCloseDetails = useCallback(() => {
    setDetailsModalOpen(false);
    setSelectedCustomerId(null);
  }, []);

  const handleViewAllCalls = useCallback((phone) => {
    setDetailsModalOpen(false);
    setSelectedCustomerId(null);
    const qs = phone ? `?view=call-logs&phone=${encodeURIComponent(phone)}` : '?view=call-logs';
    history.push(`/app/call-manager${qs}`);
  }, [history]);

  const handleJoinConversation = useCallback((customerId, userId) => {
    openChat({
      modalType: 'startConversation',
      customerId,
      userId,
      isJoining: true,
    });
  }, [openChat]);

  const handleStartConversation = useCallback((customerId, userId) => {
    openChat({
      modalType: 'startConversation',
      customerId,
      userId,
      isJoining: false,
    });
  }, [openChat]);

  const handleSendWelcomeTemplate = useCallback(async (customer) => {
    const customerId = customer.id;
    const customerLabel = customer.name?.trim() || customer.phone || 'this customer';
    const confirmed = window.confirm(
      `Send the Kannada enquiry WhatsApp template to ${customerLabel}? The customer will receive the message immediately.`
    );
    if (!confirmed) return;

    setSendingWelcomeTemplate((prev) => {
      const next = new Set(prev);
      next.add(customerId);
      return next;
    });

    try {
      const response = await fetch(`${CONFIG.API_BASE_URL}/customers/${customerId}/send-welcome-template`, {
        method: 'POST',
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.detail || data.message || 'Failed to send enquiry template');
      }

      alert('Enquiry template sent successfully');
    } catch (error) {
      console.error('Error sending enquiry template:', error);
      alert(`Failed to send enquiry template: ${error.message}`);
    } finally {
      setSendingWelcomeTemplate((prev) => {
        const next = new Set(prev);
        next.delete(customerId);
        return next;
      });
    }
  }, []);

  // Check if a name is effectively empty / N/A
  const isNameNA = (name) => {
    if (!name) return true;
    const lower = name.trim().toLowerCase();
    return ['n/a', 'na', 'unknown', 'temporary user', ''].includes(lower);
  };

  const saveCustomerNameIfNeeded = useCallback(async (customer, callName) => {
    const trimmed = callName?.trim();
    if (!trimmed || isNameNA(trimmed)) return false;

    const existingName = customer.name?.trim() || '';
    if (!isNameNA(existingName) && existingName.toLowerCase() === trimmed.toLowerCase()) {
      return false;
    }

    const detailsResp = await fetch(`${CONFIG.API_BASE_URL}/customers/${customer.id}/details`);
    if (!detailsResp.ok) {
      throw new Error('Failed to load customer details before saving name');
    }
    const details = await detailsResp.json();
    const c = details.customer;

    const payload = {
      name: trimmed,
      preferred_language: c.preferred_language || '',
      primary_address_id: c.primary_address_id ?? null,
      business_name: c.business_name || '',
      business_category: c.business_category || '',
      matched_category: c.matched_category || '',
      domain: c.domain || '',
      remarks: c.remarks || '',
      star: !!c.starred,
      discount_percentage: c.discount_percentage ?? null,
    };

    const response = await fetch(`${CONFIG.API_BASE_URL}/customers/${customer.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to save customer name');
    }

    return true;
  }, []);

  const saveCustomerNamesBulk = useCallback(async (customers, nameOverrides) => {
    const toSave = customers.filter((c) => {
      const override = nameOverrides[Number(c.id)] ?? nameOverrides[c.id] ?? '';
      const trimmed = override.trim();
      if (!trimmed || isNameNA(trimmed)) return false;
      const existingName = c.name?.trim() || '';
      if (!isNameNA(existingName) && existingName.toLowerCase() === trimmed.toLowerCase()) {
        return false;
      }
      return true;
    });

    if (toSave.length === 0) return 0;

    await Promise.all(toSave.map(async (c) => {
      const trimmed = (nameOverrides[Number(c.id)] ?? nameOverrides[c.id]).trim();
      const detailsResp = await fetch(`${CONFIG.API_BASE_URL}/customers/${c.id}/details`);
      if (!detailsResp.ok) {
        throw new Error(`Failed to load details for customer ${c.id}`);
      }
      const details = await detailsResp.json();
      const row = details.customer;

      const payload = {
        name: trimmed,
        preferred_language: row.preferred_language || '',
        primary_address_id: row.primary_address_id ?? null,
        business_name: row.business_name || '',
        business_category: row.business_category || '',
        matched_category: row.matched_category || '',
        domain: row.domain || '',
        remarks: row.remarks || '',
        star: !!row.starred,
        discount_percentage: row.discount_percentage ?? null,
      };

      const response = await fetch(`${CONFIG.API_BASE_URL}/customers/${c.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to save name for customer ${c.id}`);
      }
    }));

    return toSave.length;
  }, []);

  const resolveCallName = useCallback((customer, nameOverrides = {}) => {
    const override = nameOverrides[Number(customer.id)] ?? nameOverrides[customer.id];
    const raw = override?.trim() || customer.name || '';
    return isNameNA(raw) ? '' : raw.trim();
  }, []);

  const handleOpenVoiceCallModal = useCallback((customer) => {
    if (!customer.phone) {
      setVoiceCallModal({
        open: true,
        customer,
        callName: '',
        status: 'error',
        errorMsg: 'Customer has no phone number',
      });
      return;
    }

    const nameAvailable = !isNameNA(customer.name);
    const initialOptions = buildCustomerDialOptions(customer.phone, customer.extra_phones);
    setVoiceCallModal({
      open: true,
      customer,
      callName: nameAvailable ? customer.name : '',
      status: 'idle',
      errorMsg: '',
      dialPhone: initialOptions[0]?.phone || String(customer.phone),
      dialOptions: initialOptions,
    });
    if (customer.id) {
      fetchCustomerExtraPhones(customer.id).then((phones) => {
        const options = buildCustomerDialOptions(customer.phone, phones);
        setVoiceCallModal((prev) => {
          if (!prev.open || prev.customer?.id !== customer.id) return prev;
          const currentTail = String(prev.dialPhone || '').replace(/\D/g, '').slice(-10);
          const stillThere = options.some((opt) => String(opt.phone).slice(-10) === currentTail);
          return {
            ...prev,
            dialOptions: options,
            dialPhone: stillThere ? prev.dialPhone : (options[0]?.phone || prev.dialPhone),
          };
        });
      });
    }

    // Fetch online Customer SIMs
    setIsLoadingSims(true);
    const token = localStorage.getItem('token') || '';
    fetch(`${config.opsApiBase}/call-server/active-sims?role=customer`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        setIsLoadingSims(false);
        if (data.success && data.sims) {
          setActiveCustomerSims(data.sims);
          if (data.sims.length > 0) {
            setSelectedCustomerSim(data.sims[0].sim_number);
          } else {
            setSelectedCustomerSim('');
          }
        }
      })
      .catch(err => {
        setIsLoadingSims(false);
        console.error('Error fetching active customer SIMs:', err);
      });
  }, []);

  const handleCloseVoiceCallModal = useCallback(() => {
    setVoiceCallModal(prev => ({ ...prev, open: false }));
  }, []);

  const handleConfirmVoiceCall = useCallback(async () => {
    const { customer, callName, dialPhone } = voiceCallModal;
    if (!customer?.phone && !dialPhone) return;
    const customerId = Number(customer.id);

    setVoiceCallModal(prev => ({ ...prev, status: 'calling' }));
    setCallingCustomers(prev => new Set(prev).add(customerId));

    try {
      let phone = String(dialPhone || customer.phone);
      if (phone.startsWith('91') && phone.length === 12) {
        phone = phone.slice(2);
      }

      // Only pass company_name if a real name is provided
      const companyName = callName && !isNameNA(callName) ? callName.trim() : '';

      await saveCustomerNameIfNeeded(customer, callName);
      refetchCustomers();

      const response = await fetch(`${config.voiceCallServerBase}/call`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone_number: phone,
          company_name: companyName,
          customer_id: customer.id,
          call_type: customerNeedsFollowUpCall(customer) ? 'followup' : 'first_call',
          call_origin: 'customer',
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || data.message || 'Failed to trigger call');
      }

      applyBatchStatus(data);
      if (batchHasActivity(normalizeBatchStatus(data))) {
        batchWasActiveRef.current = true;
      }

      setVoiceCallModal(prev => ({ ...prev, status: 'success' }));
      // Auto-close after 2s on success
      setTimeout(() => {
        setVoiceCallModal(prev => ({ ...prev, open: false }));
      }, 2000);
    } catch (error) {
      console.error('Error triggering voice call:', error);
      setVoiceCallModal(prev => ({ ...prev, status: 'error', errorMsg: error.message }));
      setCallingCustomers(prev => {
        const next = new Set(prev);
        next.delete(customerId);
        return next;
      });
    }
  }, [voiceCallModal, applyBatchStatus, saveCustomerNameIfNeeded, refetchCustomers, batchWasActiveRef]);

  const handleConfirmOpsCall = useCallback(async () => {
    const { customer, callName, dialPhone } = voiceCallModal;
    if (!customer?.phone && !dialPhone) return;

    try {
      await saveCustomerNameIfNeeded(customer, callName);
      refetchCustomers();
    } catch (error) {
      console.error('Error saving customer name before Ops Call:', error);
    }

    let phone = String(dialPhone || customer.phone);
    let digits = phone.replace(/\D/g, '');
    // Normalize to E.164 format
    if (digits.length === 10) {
      digits = `91${digits}`;
    }

    try {
      const token = localStorage.getItem('token') || '';
      const response = await fetch(`${config.opsApiBase}/call-customer`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customer_phone: digits,
          customer_id: customer.id || null,
          sim_number: selectedCustomerSim || null,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        alert(`📞 Call initiated to customer ${customer.name || phone}!`);
      } else {
        alert(`Failed to place call: ${data.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error placing ops call to customer:', err);
      alert('Network error while placing call. Please check the Call Server connection.');
    }

    handleCloseVoiceCallModal();
  }, [voiceCallModal, saveCustomerNameIfNeeded, refetchCustomers, handleCloseVoiceCallModal]);

  const buildCallPayload = useCallback((customers, nameOverrides = {}) => (
    customers
      .filter(hasDialablePhone)
      .map(c => ({
        id: c.id,
        phone: String(c.phone),
        name: resolveCallName(c, nameOverrides),
      }))
  ), [resolveCallName]);

  const executeBatchCall = useCallback(async (customers, batchMode, nameOverrides = {}) => {
    const payload = buildCallPayload(customers, nameOverrides);
    if (payload.length === 0) return;

    const endpoint = batchMode === 'add' ? '/batch/add' : '/batch/start';
    const resp = await fetch(`${config.voiceCallServerBase}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customers: payload, call_origin: 'customer' }),
    });
    const data = await resp.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to start batch call');
    }
    applyBatchStatus(data);
    setSelectedCallIds(new Set());
  }, [buildCallPayload, applyBatchStatus]);

  const openBulkCallFlow = useCallback((customers, batchMode) => {
    const callable = customers.filter(hasDialablePhone);
    if (callable.length === 0) return;

    const naCustomers = callable.filter(c => isNameNA(c.name));
    if (naCustomers.length === 0) {
      executeBatchCall(callable, batchMode);
      return;
    }

    const nameOverrides = {};
    naCustomers.forEach(c => {
      nameOverrides[Number(c.id)] = '';
    });

    setBulkNameModal({
      open: true,
      customers: callable,
      nameOverrides,
      batchMode,
      status: 'idle',
      errorMsg: '',
    });
  }, [executeBatchCall]);

  const handleCloseBulkNameModal = useCallback(() => {
    setBulkNameModal(prev => ({ ...prev, open: false, status: 'idle', errorMsg: '' }));
  }, []);

  const handleConfirmBulkCall = useCallback(async () => {
    const { customers, nameOverrides, batchMode } = bulkNameModal;
    setBulkNameModal(prev => ({ ...prev, status: 'saving', errorMsg: '' }));

    try {
      const savedCount = await saveCustomerNamesBulk(customers, nameOverrides);
      if (savedCount > 0) {
        await refetchCustomers();
      }

      setBulkNameModal(prev => ({ ...prev, status: 'calling' }));
      await executeBatchCall(customers, batchMode, nameOverrides);
      setBulkNameModal(prev => ({ ...prev, open: false, status: 'idle', errorMsg: '' }));
    } catch (error) {
      console.error('Failed to start bulk call with names:', error);
      setBulkNameModal(prev => ({
        ...prev,
        status: 'error',
        errorMsg: error.message || 'Failed to save names or start calling',
      }));
    }
  }, [bulkNameModal, saveCustomerNamesBulk, refetchCustomers, executeBatchCall]);

  const toggleCallSelection = useCallback((customerId) => {
    const id = Number(customerId);
    setSelectedCallIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAllOnPage = useCallback(() => {
    const pageIds = currentCustomers
      .filter(hasDialablePhone)
      .map(c => Number(c.id));
    const allSelected = pageIds.length > 0 && pageIds.every(id => selectedCallIds.has(id));
    setSelectedCallIds(prev => {
      const next = new Set(prev);
      if (allSelected) {
        pageIds.forEach(id => next.delete(id));
      } else {
        pageIds.forEach(id => next.add(id));
      }
      return next;
    });
  }, [currentCustomers, selectedCallIds]);

  const dialableFilteredCount = React.useMemo(
    () => filteredCustomers.filter(hasDialablePhone).length,
    [filteredCustomers],
  );

  // ── Batch Call All handlers ──────────────────────────────────────────────
  const handleCallAll = useCallback(() => {
    openBulkCallFlow(filteredCustomers, 'start');
  }, [filteredCustomers, openBulkCallFlow]);

  const handleCallSelected = useCallback(() => {
    const selectedCustomers = filteredCustomers.filter(c => selectedCallIds.has(Number(c.id)));
    openBulkCallFlow(selectedCustomers, 'add');
  }, [filteredCustomers, selectedCallIds, openBulkCallFlow]);

  const handleStopBatchCall = useCallback(async () => {
    if (!VOICE_CALLS_ENABLED) return;
    try {
      const resp = await fetch(`${config.voiceCallServerBase}/batch/stop`, {
        method: 'POST',
      });
      const data = await resp.json();
      applyBatchStatus(data);
      const normalized = normalizeBatchStatus(data);
      if (!batchHasActivity(normalized)) {
        batchWasActiveRef.current = false;
        await syncCalledStatusAfterBatch();
      }
    } catch (e) {
      console.error('Failed to stop batch call:', e);
    }
  }, [applyBatchStatus, syncCalledStatusAfterBatch]);

  // Real-time call queue via SSE (in-memory on voice server — no DB).
  useEffect(() => {
    if (!VOICE_CALLS_ENABLED) return undefined;

    let disposed = false;
    let eventSource = null;
    let pollInterval = null;
    let lastDoneCount = 0;

    const handleStatus = (status) => {
      if (disposed) return;
      const normalized = normalizeBatchStatus(status);
      const isActive = batchHasActivity(normalized);
      const currentDoneCount = normalized.done.length;

      if ((batchWasActiveRef.current && !isActive) || currentDoneCount > lastDoneCount) {
        syncCalledStatusAfterBatch();
      }
      batchWasActiveRef.current = isActive;
      lastDoneCount = currentDoneCount;

      applyBatchStatus(status);

      setCallingCustomers((prev) => {
        const next = new Set(prev);
        normalized.active.forEach((id) => next.add(id));
        normalized.queued.forEach((id) => next.add(id));
        normalized.done.forEach((id) => next.delete(id));
        normalized.failed.forEach((id) => next.delete(id));
        if (!isActive) {
          next.clear();
        }
        return next;
      });
    };

    const startPollingFallback = () => {
      if (pollInterval) return;
      const poll = async () => {
        try {
          const resp = await fetch(`${config.voiceCallServerBase}/batch/status`);
          const data = await resp.json();
          handleStatus(data);
        } catch (e) {
          console.error('Batch status poll failed:', e);
        }
      };
      poll();
      pollInterval = setInterval(poll, 1500);
    };

    try {
      eventSource = new EventSource(`${config.voiceCallServerBase}/batch/stream`);
      eventSource.onmessage = (event) => {
        try {
          handleStatus(JSON.parse(event.data));
        } catch (e) {
          console.error('Failed to parse batch SSE event:', e);
        }
      };
      eventSource.onerror = () => {
        eventSource?.close();
        eventSource = null;
        startPollingFallback();
      };
    } catch (e) {
      startPollingFallback();
    }

    fetch(`${config.voiceCallServerBase}/batch/status`)
      .then(r => r.json())
      .then(handleStatus)
      .catch(() => { });

    return () => {
      disposed = true;
      eventSource?.close();
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [applyBatchStatus, syncCalledStatusAfterBatch]);

  const isLoading = customersLoading;
  const hasError = customersError;

  return (
    <div className={s.root}>
      {/* Header */}
      <div className={s.header}>
        <div className={s.headerLeft}>
          <h1 className={s.title}>
            <div className={s.titleIcon}>
              <i className="fa fa-users" />
            </div>
            Customer Management
            {selectedCity && selectedCity !== 'All' && (
              <span className={s.cityBadge}>
                <i className="fa fa-map-marker" />
                {selectedCity}
              </span>
            )}
          </h1>
          <p className={s.subtitle}>
            {selectedCity && selectedCity !== 'All'
              ? `Showing customers from ${selectedCity}`
              : 'View and manage all customers'}
          </p>
        </div>
        <div className={s.headerRight}>
          <button
            className={`${s.alertBtn} ${hasAlert ? s.active : ''}`}
            onClick={handleToggle}
          >
            <i className="fa fa-exclamation-triangle" />
            Conversations
            {flaggedCount > 0 && (
              <span className={s.alertCount}>{flaggedCount}</span>
            )}
          </button>
          <button className={s.btnNewBooking} onClick={handleNewCustomer}>
            <i className="fa fa-plus" />
            New Customer
          </button>
          <button
            className={s.refreshBtn}
            onClick={handleRetry}
            disabled={isLoading}
            title={`Auto-refreshing in ${Math.ceil(nextRefreshIn)}s`}
          >
            <i className={`fa ${isLoading ? 'fa-spinner fa-spin' : 'fa-refresh'}`} />
            {!isLoading && (
              <div className={s.refreshProgressTrack}>
                <div
                  className={s.refreshProgressBar}
                  style={{ width: `${Math.max(0, Math.min(100, (nextRefreshIn / refreshIntervalSeconds) * 100))}%` }}
                />
              </div>
            )}
          </button>
        </div>
      </div>

      <div className={s.pageBody}>
        {/* Stats Row */}
        <div className={s.statsRow}>
          {/* Total Customers — clicking clears any active filter */}
          <div
            className={`${s.statCard} ${s.primary} ${s.clickable} ${!activeFilter ? s.filterActive : ''}`}
            onClick={() => setActiveFilter(null)}
            role="button"
            title="Show all customers"
          >
            <div className={s.statIcon}>
              <i className="fa fa-users" />
            </div>
            <div className={s.statContent}>
              <div className={s.statValue}>{allCustomers.length}</div>
              <div className={s.statLabel}>Total Customers</div>
            </div>
          </div>

          <div
            className={`${s.statCard} ${s.clickable} ${activeFilter === 'b2b' ? s.filterActive : ''}`}
            onClick={() => handleFilterClick('b2b')}
            role="button"
            title="Filter B2B business portal customers"
          >
            <div className={s.statIcon}>
              <i className="fa fa-briefcase" />
            </div>
            <div className={s.statContent}>
              <div className={s.statValue}>{b2bCount}</div>
              <div className={s.statLabel}>B2B Customers</div>
            </div>
          </div>

          <div
            className={`${s.statCard} ${s.danger} ${s.clickable} ${activeFilter === 'flagged' ? s.filterActive : ''}`}
            onClick={() => handleFilterClick('flagged')}
            role="button"
            title="Filter by flagged customers"
          >
            <div className={s.statIcon}>
              <i className="fa fa-flag" />
            </div>
            <div className={s.statContent}>
              <div className={s.statValue}>{flaggedCount}</div>
              <div className={s.statLabel}>Flagged Customers</div>
            </div>
          </div>

          <div
            className={`${s.statCard} ${s.warning} ${s.clickable} ${activeFilter === 'starred' ? s.filterActive : ''}`}
            onClick={() => handleFilterClick('starred')}
            role="button"
            title="Filter important customers"
          >
            <div className={s.statIcon}>
              <i className="fa fa-star" />
            </div>
            <div className={s.statContent}>
              <div className={s.statValue}>{starredCount}</div>
              <div className={s.statLabel}>Important Customers</div>
            </div>
          </div>

          <div
            className={`${s.statCard} ${s.success} ${s.clickable} ${activeFilter === 'activeSessions' ? s.filterActive : ''}`}
            onClick={() => handleFilterClick('activeSessions')}
            role="button"
            title="Filter customers with active sessions"
          >
            <div className={s.statIcon}>
              <i className="fa fa-comments" />
            </div>
            <div className={s.statContent}>
              <div className={s.statValue}>{Object.keys(activeSessions).length}</div>
              <div className={s.statLabel}>Active Sessions</div>
            </div>
          </div>

          <div
            className={`${s.statCard} ${s.success} ${s.clickable} ${activeFilter === 'successful' ? s.filterActive : ''}`}
            onClick={() => handleFilterClick('successful')}
            role="button"
            title="Filter customers with successful conversations"
          >
            <div className={s.statIcon}>
              <i className="fa fa-check-circle" />
            </div>
            <div className={s.statContent}>
              <div className={s.statValue}>
                {statsLoading ? '–' : (conversationStats?.successful ?? 0)}
              </div>
              <div className={s.statLabel}>Successful Convos</div>
            </div>
          </div>

          <div
            className={`${s.statCard} ${s.danger} ${s.clickable} ${activeFilter === 'failed' ? s.filterActive : ''}`}
            onClick={() => handleFilterClick('failed')}
            role="button"
            title="Filter customers with failed conversations"
          >
            <div className={s.statIcon}>
              <i className="fa fa-times-circle" />
            </div>
            <div className={s.statContent}>
              <div className={s.statValue}>
                {statsLoading ? '–' : (conversationStats?.failed ?? 0)}
              </div>
              <div className={s.statLabel}>Failed Convos</div>
            </div>
          </div>

          <div
            className={`${s.moreFiltersWrap} ${moreFiltersOpen ? s.open : ''}`}
            ref={moreFiltersRef}
          >
            <button
              ref={moreBtnRef}
              type="button"
              className={`${s.moreFiltersBtn} ${(['blocked', 'marketing_opt_out', 'auto_reply_flagged']).includes(activeFilter) ? s.active : ''}`}
              onClick={toggleMoreFilters}
              title="Filter by additional customer flags"
            >
              <i className="fa fa-sliders" />
              <span className={s.moreFiltersText}>More Filters</span>
              {(['blocked', 'marketing_opt_out', 'auto_reply_flagged']).includes(activeFilter) && (
                <span className={s.moreFiltersBadge}>1</span>
              )}
              <i className={`fa fa-chevron-down ${s.moreFiltersCaret}`} />
            </button>
            {moreFiltersOpen && (
              <div
                className={s.moreFiltersMenu}
                style={menuAlignRight ? { right: 0 } : { left: 0, right: 'auto' }}
              >
                <div className={s.moreFiltersTitle}>Filter by flags</div>
                <button
                  type="button"
                  className={`${s.moreFiltersItem} ${activeFilter === 'blocked' ? s.active : ''}`}
                  onClick={() => { handleFilterClick('blocked'); setMoreFiltersOpen(false); }}
                >
                  <i className="fa fa-ban" />
                  <span className={s.moreFiltersItemLabel}>Blocked Customers</span>
                  <span className={s.moreFiltersItemCount}>{blockedCount}</span>
                  {activeFilter === 'blocked' && <i className="fa fa-check" />}
                </button>
                <button
                  type="button"
                  className={`${s.moreFiltersItem} ${activeFilter === 'marketing_opt_out' ? s.active : ''}`}
                  onClick={() => { handleFilterClick('marketing_opt_out'); setMoreFiltersOpen(false); }}
                >
                  <i className="fa fa-bell-slash" />
                  <span className={s.moreFiltersItemLabel}>Marketing Opt-Out</span>
                  <span className={s.moreFiltersItemCount}>{marketingOptOutCount}</span>
                  {activeFilter === 'marketing_opt_out' && <i className="fa fa-check" />}
                </button>
                <button
                  type="button"
                  className={`${s.moreFiltersItem} ${activeFilter === 'auto_reply_flagged' ? s.active : ''}`}
                  onClick={() => { handleFilterClick('auto_reply_flagged'); setMoreFiltersOpen(false); }}
                >
                  <i className="fa fa-robot" />
                  <span className={s.moreFiltersItemLabel}>Auto-Reply Flagged</span>
                  <span className={s.moreFiltersItemCount}>{autoReplyFlaggedCount}</span>
                  {activeFilter === 'auto_reply_flagged' && <i className="fa fa-check" />}
                </button>
                {(['blocked', 'marketing_opt_out', 'auto_reply_flagged']).includes(activeFilter) && (
                  <button
                    type="button"
                    className={s.moreFiltersClear}
                    onClick={() => { setActiveFilter(null); setMoreFiltersOpen(false); }}
                  >
                    <i className="fa fa-times" /> Clear filter
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Conversation Stats Time Selector */}
        <div className={s.statsTimeRow}>
          <span className={s.statsTimeLabel}>
            <i className="fa fa-clock-o" /> Conversation stats window:
          </span>
          {[0.5, 1, 3, 6, 12, 24].map((h) => (
            <button
              key={h}
              className={`${s.statsTimeBtn} ${statsHours === h ? s.active : ''}`}
              onClick={() => { setStatsHours(h); }}
            >
              {h < 1 ? `${h * 60}m` : `${h}h`}
            </button>
          ))}
        </div>

      {/* Search Section */}
      <div className={s.searchSection}>
        <div className={s.searchWrapper}>
          <div className={s.searchInputGroup}>
            <span className={s.searchIcon}>
              <i className="fa fa-search" />
            </span>
            <input
              type="text"
              className={s.searchInput}
              placeholder="Search by name, ID, phone, remarks or call summary..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className={s.clearBtn} onClick={() => setSearchTerm('')}>
                <i className="fa fa-times" />
              </button>
            )}
          </div>
          <div className={s.sortGroup}>
            <span className={s.sortLabel}>Sort by</span>
            <select
              className={s.sortSelect}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="recentConversation">Most Recent Conversation</option>
              <option value="id">Customer ID</option>
            </select>
          </div>
          <div className={s.toggleGroup}>
            <button 
              className={`${s.toggleBtn} ${callStatusFilter === 'all' ? s.active : ''}`}
              onClick={() => setCallStatusFilter('all')}
            >
              All
            </button>
            <button 
              className={`${s.toggleBtn} ${callStatusFilter === 'called' ? s.active : ''}`}
              onClick={() => setCallStatusFilter('called')}
            >
              Called
            </button>
            <button 
              className={`${s.toggleBtn} ${callStatusFilter === 'not_called' ? s.active : ''}`}
              onClick={() => setCallStatusFilter('not_called')}
            >
              Not Called
            </button>
            <button 
              className={`${s.toggleBtn} ${callStatusFilter === 'calling' ? s.active : ''}`}
              onClick={() => setCallStatusFilter('calling')}
            >
              Calling
            </button>
          </div>
          <button
            className={s.syncBtn}
            onClick={handleSyncCalledStatus}
            disabled={syncingCalled || isLoading}
            title="Sync called status from database (updates Not Called / Called filters)"
          >
            <i className={`fa ${syncingCalled ? 'fa-spinner fa-spin' : 'fa-cloud-download'}`} />
            Sync
          </button>
          {selectedCallIds.size > 0 && (
            <button className={s.callSelectedBtn} onClick={handleCallSelected}>
              <i className="fa fa-phone" /> Call Selected ({selectedCallIds.size})
            </button>
          )}
          {VOICE_CALLS_ENABLED && !hasBatchActivity && dialableFilteredCount > 0 && (
            <button className={s.callAllBtn} onClick={handleCallAll}>
              <i className="fa fa-phone" /> Call All
            </button>
          )}
          {VOICE_CALLS_ENABLED && hasBatchActivity && (
            <button className={s.stopBatchBtn} onClick={handleStopBatchCall}>
              <i className="fa fa-stop" /> Stop Calling
            </button>
          )}
          {(searchTerm || activeFilter || callStatusFilter !== 'all') && (
            <span className={s.resultBadge}>
              {filteredCustomers.length} result{filteredCustomers.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* Batch Call Progress Bar */}
        {VOICE_CALLS_ENABLED && hasBatchActivity && (
          <div className={s.batchProgressContainer}>
            <div className={s.batchProgressHeader}>
              <span className={s.batchProgressTitle}>
                <i className="fa fa-phone" /> Call Queue
              </span>
              <span className={s.batchProgressStats}>
                {batchCallsCompleted} completed • {batchCallsInProgress} in call • {batchCallsLeft} left in queue
              </span>
            </div>
            <div className={s.batchProgressBar}>
              <div 
                className={s.batchProgressFill} 
                style={{ width: `${batchProgressPct}%` }}
              />
            </div>
            <div className={s.batchProgressFooter}>
              {batchProgressPct}% complete ({batchCallsCompleted} of {batchStatus.total})
            </div>
          </div>
        )}
      </div>

      {/* Table Card */}
      <div className={s.tableCard}>
        {hasError ? (
          <div className={s.errorState}>
            <div className={s.errorIcon}>
              <i className="fa fa-exclamation-circle" />
            </div>
            <p>Error loading customers: {customersError}</p>
            <button className={s.retryBtn} onClick={handleRetry}>
              <i className="fa fa-refresh" /> Retry
            </button>
          </div>
        ) : isLoading && filteredCustomers.length === 0 ? (
          <div className={s.loadingState}>
            <div className={s.spinner} />
            <p>Loading customers...</p>
          </div>
        ) : filteredCustomers.length === 0 ? (
          <div className={s.emptyState}>
            <div className={s.emptyIcon}>
              <i className="fa fa-inbox" />
            </div>
            <h3>No customers found</h3>
            <p>
              {searchTerm && activeFilter
                ? `No customers match "${searchTerm}" in this filter`
                : searchTerm
                ? `No customers match "${searchTerm}"`
                : activeFilter
                ? 'No customers match the selected filter'
                : 'There are no customers to display'}
            </p>
          </div>
        ) : (
          <>
            <div className={s.tableScrollHint}>
              <i className="fa fa-arrows-h" />
              Scroll horizontally to see all columns. ID, name, and actions stay pinned.
            </div>
            <div className={s.tableWrapper}>
            <table className={s.dataTable}>
              <thead>
                <tr>
                  <th className={s.selectCol} aria-label="Select">
                    <input
                      type="checkbox"
                      className={s.rowCheckbox}
                      onChange={toggleSelectAllOnPage}
                      checked={
                        currentCustomers.filter(hasDialablePhone).length > 0
                        && currentCustomers
                          .filter(hasDialablePhone)
                          .every(c => selectedCallIds.has(Number(c.id)))
                      }
                      title="Select all on this page"
                    />
                  </th>
                  <th className={`${s.colId} ${s.colStickyLeft}`}><i className="fa fa-hashtag" />ID</th>
                  <th className={`${s.colB2b} ${s.colB2bSticky}`} aria-label="Account type" />
                  <th className={`${s.colName} ${s.colNameSticky}`}><i className="fa fa-user" />Name</th>
                  <th className={s.colPhone}><i className="fa fa-phone" />Phone</th>
                  <th className={s.colLang} title="Language"><i className="fa fa-language" />Lang</th>
                  <th className={s.colAddress}><i className="fa fa-map-marker" />Address</th>
                  <th className={s.colCompact} title="RFM Score"><i className="fa fa-bar-chart" />RFM</th>
                  <th className={s.colCompact} title="Bookings"><i className="fa fa-calendar" />Bkgs</th>
                  <th className={s.colCompact}><i className="fa fa-info-circle" />Status</th>
                  <th className={s.colCompact} title="Times Called"><i className="fa fa-phone" />Calls</th>
                  <th className={`${s.colActions} ${s.colActionsSticky}`}><i className="fa fa-cog" />Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentCustomers.map((customer) => {
                  const hasActiveSession = activeSessions[customer.id];
                  const customerId = Number(customer.id);
                  const isCallSelectable = hasDialablePhone(customer);
                  const batchState = getCustomerBatchState(batchStatus, customerId);
                  const isSoloCalling = callingCustomers.has(customerId);
                  const isBatchActive = batchState === 'active' || isSoloCalling;
                  const isBatchQueued = batchState === 'queued';
                  const isBatchDone = batchState === 'done';
                  const isBatchFailed = batchState === 'failed';
                  const botCalls = Number(customer.voice_call_count) || 0;
                  const opCalls = Number(customer.operator_call_count) || 0;
                  const timesCalled = botCalls + opCalls + (pendingCallCounts[customerId] || 0);
                  const callTimeLabel = formatLatestCallLabel(customer.call_summary);
                  return (
                    <tr
                      key={customer.id}
                      className={[
                        customer.is_blocked ? s.blockedRow : '',
                        customer.starred ? s.starredRow : '',
                        customer.is_auto_reply_flagged ? s.autoReplyRow : '',
                        isBatchActive ? s.batchActiveRow : '',
                        isBatchQueued ? s.batchQueuedRow : '',
                        (isBatchDone || isBatchFailed) && !isBatchActive && !isBatchQueued ? s.batchDoneRow : '',
                      ].filter(Boolean).join(' ')}
                    >
                      <td className={s.selectCol}>
                        {isCallSelectable && (
                          <input
                            type="checkbox"
                            className={s.rowCheckbox}
                            checked={selectedCallIds.has(customerId)}
                            onChange={() => toggleCallSelection(customerId)}
                          />
                        )}
                      </td>
                      <td className={`${s.idCell} ${s.colId} ${s.colStickyLeft}`}>
                        <div className={s.idCellContent}>
                          {(customer.starred || customer.is_auto_reply_flagged) && (
                            <div className={s.idCellIcons}>
                              {customer.starred && (
                                <i className={`fa fa-star ${s.idStarIcon}`} title="Important customer" aria-label="Important customer" />
                              )}
                              {customer.is_auto_reply_flagged && (
                                <span
                                  className={s.idAutoReplyBadge}
                                  title="WhatsApp auto-reply flagged"
                                  aria-label="Auto-reply flagged"
                                >
                                  🤖
                                </span>
                              )}
                            </div>
                          )}
                          <span className={s.idCellNumber}>{customer.id}</span>
                        </div>
                      </td>
                      <td className={`${s.colB2b} ${s.colB2bSticky}`}>
                        {customer.is_b2b ? (
                          <span className={s.b2bBadge} title="B2B Business Account">
                            <i className="fa fa-star" /> B2B
                          </span>
                        ) : null}
                      </td>
                      <td className={`${s.colName} ${s.colNameSticky}`}>
                        <div className={s.nameCell}>
                          <span className={s.customerName}>
                            {customer.name || 'N/A'}
                          </span>
                          {customer.business_name && customer.business_name.trim() && customer.business_name.trim() !== (customer.name || '').trim() && (
                            <span className={s.businessName}>{customer.business_name}</span>
                          )}
                          {customer.is_new_customer && (
                            <span className={s.newBadge}>
                              <i className="fa fa-star" /> New
                            </span>
                          )}
                          {customer.marketing_opt_out && (
                            <span className={s.optedOutBadge} title={customer.marketing_opted_out_at ? `Opted out on ${new Date(customer.marketing_opted_out_at).toLocaleDateString()}` : 'Opted out of marketing calls'}>
                              <i className="fa fa-bell-slash" /> Opted Out
                            </span>
                          )}
                          {callTimeLabel && (
                            <span
                              className={s.callTimeBadge}
                              title={customer.call_summary || undefined}
                            >
                              <i className="fa fa-microphone" aria-hidden />
                              <span className={s.callTimeBot}>Voice bot</span>
                              <span className={s.callTimeSep}>·</span>
                              <span className={s.callTimeWhen}>{callTimeLabel}</span>
                            </span>
                          )}
                        </div>
                      </td>
                      <td className={s.colPhone}>
                        <div className={s.phoneCell}>
                          <i className="fa fa-phone" />
                          {customer.phone || 'N/A'}
                        </div>
                      </td>
                      <td className={s.colLang}>
                        <div className={s.languageCell}>
                          <i className="fa fa-language" />
                          {customer.preferred_language || 'N/A'}
                        </div>
                      </td>
                      <td className={`${s.addressCell} ${s.colAddress}`}>
                        {customer.primary_address ? (
                          <div className={s.addressText} title={customer.primary_address}>
                            <i className="fa fa-map-marker" />
                            <span>{customer.primary_address}</span>
                          </div>
                        ) : (
                          <span className={s.noAddress}>N/A</span>
                        )}
                      </td>
                      <td className={s.colCompact}>
                        <span className={`${s.ratingBadge} ${customer.rfm_base_score !== null && customer.rfm_base_score !== undefined ? s.hasRating : s.noRating}`}>
                          {customer.rfm_base_score !== null && customer.rfm_base_score !== undefined ? (
                            <>
                              <i className="fa fa-bar-chart" />
                              {customer.rfm_base_score}
                            </>
                          ) : (
                            'No Score'
                          )}
                        </span>
                      </td>
                      <td className={s.colCompact}>
                        <span className={s.bookingsBadge}>
                          <i className="fa fa-calendar" />
                          {customer.booking_count || 0}
                        </span>
                      </td>
                      <td className={s.colCompact}>
                        <span className={`${s.statusBadge} ${customer.is_blocked ? s.blocked : (customer.flagged ? s.flagged : s.active)}`}>
                          {customer.is_blocked ? 'Blocked' : (customer.flagged ? 'Flagged' : 'Active')}
                        </span>
                      </td>
                      <td className={s.colCompact}>
                        <span
                          className={s.callCountBadge}
                          title={`Bot: ${botCalls}, Operator: ${opCalls}`}
                        >
                          <i className="fa fa-phone" />
                          {timesCalled}
                        </span>
                      </td>
                      <td className={`${s.colActions} ${s.colActionsSticky}`}>
                        <div className={s.actionsCell}>
                          {hasActiveSession ? (
                            <button
                              className={`${s.iconActionBtn} ${s.chatBtn} ${s.joinChat}`}
                              onClick={() => handleJoinConversation(customer.id, hasActiveSession.user_id)}
                              title="Join conversation"
                            >
                              <i className="fa fa-comments" />
                            </button>
                          ) : (
                            <button
                              className={`${s.iconActionBtn} ${s.chatBtn} ${s.startChat}`}
                              onClick={() => handleStartConversation(customer.id, customer.user_id)}
                              title="Start conversation"
                            >
                              <i className="fa fa-comment" />
                            </button>
                          )}
                          <button
                            className={s.iconActionBtn}
                            onClick={() => handleViewDetails(customer.id)}
                            title="View details"
                          >
                            <i className="fa fa-info-circle" />
                          </button>
                          <button
                            className={`${s.iconActionBtn} ${s.callBtn}`}
                            onClick={() => handleOpenVoiceCallModal(customer)}
                            disabled={isBatchActive || isBatchQueued}
                            title={`Call ${customer.name || customer.phone}`}
                          >
                            <i className={`fa ${callingCustomers.has(customerId) || isBatchActive ? 'fa-spinner fa-spin' : 'fa-phone'}`} />
                          </button>
                          <button
                            className={`${s.iconActionBtn} ${s.templateBtn}`}
                            onClick={() => handleSendWelcomeTemplate(customer)}
                            disabled={sendingWelcomeTemplate.has(customer.id)}
                            title="Send enquiry WhatsApp"
                          >
                            <i className={`fa ${sendingWelcomeTemplate.has(customer.id) ? 'fa-spinner fa-spin' : 'fa-whatsapp'}`} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </>
        )}
        
        {/* Pagination Controls */}
        {!isLoading && !hasError && filteredCustomers.length > itemsPerPage && (
          <div className={s.pagination}>
            <button
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
              className={s.pageBtn}
            >
              <i className="fa fa-chevron-left" /> Previous
            </button>
            <span className={s.pageInfo}>
              Page
              <input
                type="number"
                min={1}
                max={totalPages}
                value={pageInput}
                onChange={handlePageInputChange}
                onKeyDown={handlePageInputKeyDown}
                onBlur={handlePageInputBlur}
                className={s.pageInput}
              />
              of {totalPages}
            </span>
            <button
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
              className={s.pageBtn}
            >
              Next <i className="fa fa-chevron-right" />
            </button>
          </div>
        )}
      </div>
      </div>

      {/* Customer Details Modal */}
      <CustomerDetailsModal
        isOpen={detailsModalOpen}
        toggle={handleCloseDetails}
        customerId={selectedCustomerId}
        onCustomerUpdated={refetchCustomers}
        onViewAllCalls={handleViewAllCalls}
      />

      {/* New Customer Modal */}
      <NewCustomerModal
        isOpen={newCustomerModalOpen}
        toggle={handleCloseNewCustomer}
        onSuccess={handleCustomerCreated}
      />

      {/* Voice Call Modal */}
      {voiceCallModal.open && (
        <div className={s.vcOverlay} onClick={handleCloseVoiceCallModal}>
          <div className={s.vcModal} onClick={(e) => e.stopPropagation()}>
            {/* Close button */}
            <button className={s.vcCloseBtn} onClick={handleCloseVoiceCallModal}>
              <i className="fa fa-times" />
            </button>

            {/* Icon */}
            <div className={`${s.vcIconCircle} ${voiceCallModal.status === 'calling' ? s.vcCalling :
                voiceCallModal.status === 'success' ? s.vcSuccess :
                  voiceCallModal.status === 'error' ? s.vcError : ''
              }`}>
              <i className={`fa ${voiceCallModal.status === 'calling' ? 'fa-spinner fa-spin' :
                  voiceCallModal.status === 'success' ? 'fa-check' :
                    voiceCallModal.status === 'error' ? 'fa-exclamation-triangle' :
                      'fa-phone'
                }`} />
            </div>

            {/* Title */}
            <h3 className={s.vcTitle}>
              {voiceCallModal.status === 'calling' ? 'Placing Call...' :
                voiceCallModal.status === 'success' ? 'Call Triggered!' :
                  voiceCallModal.status === 'error' ? 'Call Failed' :
                    'Voice Call'}
            </h3>

            {/* Customer info */}
            {voiceCallModal.customer && (
              <div className={s.vcCustomerInfo}>
                <div className={s.vcPhoneRow}>
                  <i className="fa fa-phone" />
                  <span>{voiceCallModal.dialPhone || voiceCallModal.customer.phone || 'No phone'}</span>
                </div>
                {!isNameNA(voiceCallModal.customer.name) && (
                  <div className={s.vcNameRow}>
                    <i className="fa fa-user" />
                    <span>{voiceCallModal.customer.name}</span>
                  </div>
                )}
              </div>
            )}

            {/* N/A name — input to add name */}
            {voiceCallModal.status === 'idle' && voiceCallModal.customer && isNameNA(voiceCallModal.customer.name) && (
              <div className={s.vcNameInputSection}>
                <p className={s.vcNameHint}>
                  <i className="fa fa-info-circle" />
                  No name on file. Add one to save it and greet them by name, or skip to call directly.
                </p>
                <input
                  type="text"
                  className={s.vcNameInput}
                  placeholder="Enter customer name (optional)"
                  value={voiceCallModal.callName}
                  onChange={(e) => setVoiceCallModal(prev => ({ ...prev, callName: e.target.value }))}
                  autoFocus
                />
              </div>
            )}

            {/* Error message */}
            {voiceCallModal.status === 'error' && voiceCallModal.errorMsg && (
              <p className={s.vcErrorMsg}>{voiceCallModal.errorMsg}</p>
            )}

            {/* Success message */}
            {voiceCallModal.status === 'success' && (
              <p className={s.vcSuccessMsg}>
                📞 Call dispatched to {voiceCallModal.customer?.name && !isNameNA(voiceCallModal.customer.name)
                  ? voiceCallModal.customer.name
                  : voiceCallModal.customer?.phone}
              </p>
            )}

            {voiceCallModal.status === 'idle' && (
              <CustomerDialNumberPicker
                options={voiceCallModal.dialOptions}
                value={voiceCallModal.dialPhone}
                onChange={(phone) => setVoiceCallModal((prev) => ({ ...prev, dialPhone: phone }))}
              />
            )}

            {/* Outbound SIM Selector */}
            {voiceCallModal.status === 'idle' && (
              <div className={s.vcSimSection}>
                <div className={s.vcSimHeader}>
                  <i className="fa fa-signal" />
                  <span>Choose Outbound Phone (SIM):</span>
                </div>
                {isLoadingSims ? (
                  <div className={s.vcSimLoading}>
                    <i className="fa fa-spinner fa-spin" /> Checking connected phones...
                  </div>
                ) : activeCustomerSims.length > 0 ? (
                  <div className={s.vcSimList}>
                    {activeCustomerSims.map((sim, idx) => {
                      const isSelected = selectedCustomerSim === sim.sim_number;
                      return (
                        <button
                          key={sim.sim_number}
                          type="button"
                          className={`${s.vcSimBtn} ${isSelected ? s.vcSimBtnActive : ''}`}
                          onClick={() => setSelectedCustomerSim(sim.sim_number)}
                        >
                          <span className={s.vcSimRadio}>
                            {isSelected && <span className={s.vcSimRadioDot} />}
                          </span>
                          <span className={s.vcSimLabel}>Phone {idx + 1}:</span>
                          <span className={s.vcSimNumber}>{sim.sim_number}</span>
                          <span className={s.vcSimOnlineDot} title="Online" />
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className={s.vcSimEmpty}>
                    <i className="fa fa-exclamation-circle" />
                    <span>No phone lines are currently available. Please try again later.</span>
                  </div>
                )}
              </div>
            )}

            {/* Buttons */}
            {voiceCallModal.status === 'idle' && (
              <div className={s.vcBtnRow}>
                <button className={s.vcCancelBtn} onClick={handleCloseVoiceCallModal}>
                  Cancel
                </button>
                <button
                  className={s.vcConfirmBtn}
                  onClick={handleConfirmVoiceCall}
                  disabled={!voiceCallModal.dialPhone && !voiceCallModal.customer?.phone}
                  title="Trigger automated AI bot call"
                >
                  <i className="fa fa-android" />
                  Bot Call
                </button>
                <button
                  className={s.vcOpsBtn}
                  onClick={handleConfirmOpsCall}
                  disabled={!voiceCallModal.dialPhone && !voiceCallModal.customer?.phone}
                  title="Call directly via FaceTime/phonelink"
                >
                  <i className="fa fa-phone" />
                  Ops Call
                </button>
              </div>
            )}

            {voiceCallModal.status === 'error' && (
              <div className={s.vcBtnRow}>
                <button className={s.vcCancelBtn} onClick={handleCloseVoiceCallModal}>
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bulk call — add names for N/A customers before Call All / Call Selected */}
      {bulkNameModal.open && (
        <div className={s.vcOverlay} onClick={handleCloseBulkNameModal}>
          <div className={`${s.vcModal} ${s.bulkNameModal}`} onClick={(e) => e.stopPropagation()}>
            <button className={s.vcCloseBtn} onClick={handleCloseBulkNameModal}>
              <i className="fa fa-times" />
            </button>

            <div className={`${s.vcIconCircle} ${bulkNameModal.status === 'error' ? s.vcError : ''}`}>
              <i className={`fa ${bulkNameModal.status === 'saving' || bulkNameModal.status === 'calling'
                  ? 'fa-spinner fa-spin'
                  : 'fa-users'
                }`} />
            </div>

            <h3 className={s.vcTitle}>Add Names Before Calling</h3>
            <p className={s.bulkNameSubtitle}>
              {Object.keys(bulkNameModal.nameOverrides).length} of {bulkNameModal.customers.length} customers
              are missing names. Names you enter are saved to the customer record.
            </p>

            <div className={s.bulkNameList}>
              {bulkNameModal.customers
                .filter(c => isNameNA(c.name))
                .map((customer) => (
                  <div key={customer.id} className={s.bulkNameRow}>
                    <div className={s.bulkNameMeta}>
                      <span className={s.bulkNameId}>#{customer.id}</span>
                      <span className={s.bulkNamePhone}>
                        <i className="fa fa-phone" /> {customer.phone}
                      </span>
                    </div>
                    <input
                      type="text"
                      className={s.bulkNameInput}
                      placeholder="Enter name (optional)"
                      value={bulkNameModal.nameOverrides[Number(customer.id)] || ''}
                      onChange={(e) => setBulkNameModal(prev => ({
                        ...prev,
                        nameOverrides: {
                          ...prev.nameOverrides,
                          [Number(customer.id)]: e.target.value,
                        },
                      }))}
                      disabled={bulkNameModal.status === 'saving' || bulkNameModal.status === 'calling'}
                    />
                  </div>
                ))}
            </div>

            {bulkNameModal.status === 'error' && bulkNameModal.errorMsg && (
              <p className={s.vcErrorMsg}>{bulkNameModal.errorMsg}</p>
            )}

            {bulkNameModal.status === 'idle' && (
              <div className={s.vcBtnRow}>
                <button className={s.vcCancelBtn} onClick={handleCloseBulkNameModal}>
                  Cancel
                </button>
                <button className={s.vcConfirmBtn} onClick={handleConfirmBulkCall}>
                  <i className="fa fa-phone" />
                  Start Calling ({bulkNameModal.customers.length})
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Customers;















